/**
 * The LinkedIn "Add to Profile" link, with the certificate's own values in it.
 *
 * WHY THIS EXISTS
 * ---------------
 * The button used to open a bare `…/profile/add?startTask=CERTIFICATION_NAME`. That link carries no
 * certificate data at all, so the LinkedIn form it opens is necessarily empty and the recipient has
 * to retype six to eight values. The data was never lost in a redirect and it was never sent with
 * the wrong names: it was never sent. The copy helper beside the button held the values instead.
 *
 * This module builds the link LinkedIn's own Add-to-Profile builder documents, so the values travel
 * WITH the click. Everything the form needs is a query parameter; nothing about it is an API call,
 * an OAuth flow, a browser extension, a userscript or a write to anyone's profile. The member still
 * lands on LinkedIn's real form, still reviews it, and still presses Save themselves.
 *
 * THE PARAMETER NAMES ARE NOT INTERCHANGEABLE
 * -------------------------------------------
 * `certId` and `certUrl` are the credential identifier and the credential verification URL. The
 * plausible-looking `credentialId` / `credentialUrl` are NOT the same keys and LinkedIn ignores
 * them. Likewise `organizationId` is a NUMBER — the company page's own id. A slug
 * (`happyplacetowork`), a page URL, a display name or a full `urn:li:organization:…` sent in that
 * slot is not an id; LinkedIn drops it and the organisation comes back unselected, which is exactly
 * the failure this is meant to remove. `organizationId` and `organizationName` are mutually
 * exclusive: an organisation with a LinkedIn Page uses the id, one without uses the name.
 *
 * DATES ARE SLICED, NEVER PARSED
 * ------------------------------
 * `issueDate` and `expiresAt` are date-only strings (`YYYY-MM-DD`). Constructing a `Date` from one
 * and reading `getMonth()` shifts it by a timezone and can move January 1st into the previous
 * December — a wrong year on a certificate. The year and month are therefore taken as substrings,
 * so the value on LinkedIn is the value in the record regardless of where the browser is.
 *
 * FAIL-CLOSED
 * -----------
 * A missing title, issue date, public code or public URL returns `url: null` and names what is
 * missing. Opening an empty form and calling it done is the one outcome this must never produce.
 */

import { verifiedOrganizationIdForPage } from './linkedin-organization';

/** A LinkedIn organisation id is a positive integer, and nothing else is accepted as one. */
const NUMERIC_ORGANIZATION_ID = /^[1-9][0-9]{0,19}$/;
/** Date-only, as stored. Anything else is treated as absent rather than guessed at. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const LINKEDIN_ADD_TO_PROFILE_ORIGIN = 'https://www.linkedin.com/profile/add';

export interface AddToProfileInput {
  /** The certificate's real name, as the record carries it. */
  title: string;
  /** `YYYY-MM-DD`, the record's own issue date. */
  issueDate: string;
  /** `YYYY-MM-DD` or null. Null means perpetual: no expiration parameters are sent at all. */
  expiresAt: string | null;
  /** The public code shown to the holder — the durable certificate identifier. */
  publicCode: string;
  /** The canonical public verification page. Never a claim link, an admin screen or a localhost. */
  publicUrl: string;
  /** The verified numeric LinkedIn company id, or null when it has not been configured yet. */
  organizationId: string | null;
  /** The issuer's LinkedIn name. Used only as the documented fallback when no id is configured. */
  organizationName: string | null;
}

export type OrganizationMode = 'id' | 'name' | 'none';

export interface AddToProfileLink {
  /** The finished link, or null when a required value is missing. */
  url: string | null;
  /** Which required values were absent. Empty when `url` is non-null. */
  missing: string[];
  /**
   * How the issuing organisation was addressed.
   *
   * `id` is the only mode that makes LinkedIn preselect the official Page and attach its logo.
   * `name` fills the text but leaves the member to pick the Page from the suggestions, so a surface
   * that shows the button must not describe that mode as "organisation already selected".
   */
  organizationMode: OrganizationMode;
}

function yearMonth(dateOnly: string | null): { year: string; month: string } | null {
  if (!dateOnly) return null;
  const m = DATE_ONLY.exec(dateOnly);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  /* LinkedIn wants 1–12 without a leading zero; the year is taken verbatim. */
  return { year: m[1] as string, month: String(month) };
}

/**
 * A public verification URL we are willing to hand to LinkedIn.
 *
 * https only, a real host, and no query or fragment — a claim token or a session value has no
 * business travelling to a third party, and the canonical page never needs one.
 */
function publicUrlIsAcceptable(raw: string): boolean {
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return false;
    if (!u.hostname.includes('.')) return false;
    if (u.search !== '' || u.hash !== '') return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Builds the link. `URL` + `URLSearchParams` do the encoding exactly once, which is what keeps the
 * Turkish characters, the ® sign and the nested `certUrl` correct — hand-built strings are where
 * double-encoding comes from.
 */
export function buildAddToProfileUrl(input: AddToProfileInput): AddToProfileLink {
  const missing: string[] = [];
  const title = input.title.trim();
  const publicCode = input.publicCode.trim();
  const publicUrl = input.publicUrl.trim();
  const issued = yearMonth(input.issueDate);

  if (!title) missing.push('title');
  if (!issued) missing.push('issueDate');
  if (!publicCode) missing.push('publicCode');
  if (!publicUrl || !publicUrlIsAcceptable(publicUrl)) missing.push('publicUrl');

  const orgId = (input.organizationId ?? '').trim();
  const orgName = (input.organizationName ?? '').trim();
  const organizationMode: OrganizationMode = NUMERIC_ORGANIZATION_ID.test(orgId)
    ? 'id'
    : orgName
      ? 'name'
      : 'none';

  if (missing.length > 0) return { url: null, missing, organizationMode };

  const url = new URL(LINKEDIN_ADD_TO_PROFILE_ORIGIN);
  const q = url.searchParams;
  q.set('startTask', 'CERTIFICATION_NAME');
  q.set('name', title);
  /* Exactly one organisation parameter — sending both is documented as invalid. */
  if (organizationMode === 'id') q.set('organizationId', orgId);
  else if (organizationMode === 'name') q.set('organizationName', orgName);
  q.set('issueYear', (issued as { year: string }).year);
  q.set('issueMonth', (issued as { month: string }).month);
  const expires = yearMonth(input.expiresAt);
  /* A perpetual certificate sends NO expiration keys; empty ones would read as a blank expiry. */
  if (expires) {
    q.set('expirationYear', expires.year);
    q.set('expirationMonth', expires.month);
  }
  q.set('certId', publicCode);
  q.set('certUrl', publicUrl);
  return { url: url.toString(), missing: [], organizationMode };
}

/* ------------------------------------------------------------------ the product's own shorthand */

/**
 * The organisation half of the configuration, as the screens have it.
 *
 * Kept as its own type so a screen cannot accidentally pass a template, a release or a page URL:
 * the numeric id is established once during integration setup and read from there, and the name is
 * only ever the documented fallback.
 */
export interface IssuerLinkedInConfig {
  /** The issuer's own stored numeric id, when the deployment has filled it in. */
  linkedinOrganizationId: string | null;
  /** The issuer's configured LinkedIn Page, used to find the verified id when the above is null. */
  linkedinPageUrl: string | null;
  linkedinIssuerName: string;
}

/** The fields a credential must expose to be describable to LinkedIn. */
export interface CredentialForLinkedIn {
  publicCode: string;
  canonicalPublicUrl: string;
  issueDate: string;
  expiresAt: string | null;
}

/**
 * One call for every screen that shows the button.
 *
 * The title comes in separately because the screens already localise it through `credTitle`, and
 * the issuer values prefer the credential's OWN immutable release snapshot over today's settings:
 * an old certificate must describe the issuer as it was when the record was made, not as the
 * settings screen has since been edited.
 */
export function addToProfileLinkFor(
  credential: CredentialForLinkedIn,
  title: string,
  config: IssuerLinkedInConfig,
): AddToProfileLink {
  /*
   * One resolution order, here rather than in each screen: the issuer's own stored id first, then
   * the verified configuration directory for the Page that issuer is configured with. The pure
   * builder below still receives the id as a plain argument and knows no organisation by name.
   */
  const organizationId =
    config.linkedinOrganizationId ?? verifiedOrganizationIdForPage(config.linkedinPageUrl);
  return buildAddToProfileUrl({
    title,
    issueDate: credential.issueDate,
    expiresAt: credential.expiresAt,
    publicCode: credential.publicCode,
    publicUrl: credential.canonicalPublicUrl,
    organizationId,
    organizationName: config.linkedinIssuerName,
  });
}

/**
 * The verified numeric company id out of the issuer's `organizationReference`.
 *
 * The reference is a configured, audited object — not a free-text field — so this reads it rather
 * than accepting whatever an administrator typed. A `urn:li:organization:1337` is tolerated as a
 * SOURCE for the number (it is the same identifier written differently) but the URN itself never
 * reaches the link; anything that is not a positive integer after that comes back null.
 */
export function organizationIdFromReference(reference: Record<string, unknown> | null): string | null {
  if (!reference) return null;
  const direct = reference.organizationId;
  if (typeof direct === 'string' && NUMERIC_ORGANIZATION_ID.test(direct.trim())) return direct.trim();
  if (typeof direct === 'number' && Number.isInteger(direct) && direct > 0) return String(direct);
  const urn = reference.urn;
  if (typeof urn === 'string') {
    const m = /^urn:li:organization:([1-9][0-9]{0,19})$/.exec(urn.trim());
    if (m) return m[1] as string;
  }
  return null;
}
